import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import type { AppDispatch, RootState } from "../store";
import { searchUangJalanReports } from "../store/features/uangJalanReportSlice";
import { fetchOperationsTeams } from "../store/features/operationsTeamSlice";
import DetailPageHeader from "../components/common/DetailPageHeader";
import UangJalanReportTable from "../components/uangJalanReports/UangJalanReportTable";
import { TestId } from "../constants/testIds";
import { canMutateOrders } from "../types/user";
import type { UangJalanReportSearchFilters } from "../types/uangJalanReport";
import type { PaginationParam } from "../types/paginationResponse";
import { getActionRequiredReportStatusesForUser } from "../utils/actionRequiredStatuses";

type TranslationKeys = keyof typeof translations.en;

const DEFAULT_PAGE_SIZE = 10;

const headerButtonSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  borderRadius: 1.5,
};

const UangJalanReportsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const { uangJalanReports, loading, error, pagination } = useSelector(
    (state: RootState) => state.uangJalanReports,
  );
  const { operationsTeams } = useSelector((state: RootState) => state.operationsTeams);

  const isActionRequired = location.pathname === "/uang-jalan-reports/action-required";
  const actionRequiredStatuses = useMemo(
    () => getActionRequiredReportStatusesForUser(user),
    [user],
  );

  const [filters, setFilters] = useState<UangJalanReportSearchFilters>({
    status: searchParams.get("status") || "",
    teamOperationsId: searchParams.get("teamOperationsId") || "",
  });
  const [paginationState, setPaginationState] = useState<PaginationParam>({
    page: Number(searchParams.get("page")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || DEFAULT_PAGE_SIZE,
  });

  const canCreateReport = canMutateOrders(user);

  const effectiveFilters = useMemo((): UangJalanReportSearchFilters => {
    if (!isActionRequired || actionRequiredStatuses.length === 0) {
      return filters;
    }
    return {
      teamOperationsId: filters.teamOperationsId,
      statuses: actionRequiredStatuses,
    };
  }, [filters, isActionRequired, actionRequiredStatuses]);

  useEffect(() => {
    dispatch(fetchOperationsTeams());
  }, [dispatch]);

  const actionRequiredEmpty = isActionRequired && actionRequiredStatuses.length === 0;

  useEffect(() => {
    if (actionRequiredEmpty) {
      return;
    }
    dispatch(searchUangJalanReports({ filters: effectiveFilters, pagination: paginationState }));
  }, [actionRequiredEmpty, dispatch, effectiveFilters, paginationState]);

  const handleSearch = async (
    newFilters: UangJalanReportSearchFilters,
    newPagination: PaginationParam,
  ) => {
    const newParams = new URLSearchParams();
    if (newFilters.status) {
      newParams.set("status", newFilters.status);
    }
    if (newFilters.teamOperationsId) {
      newParams.set("teamOperationsId", newFilters.teamOperationsId);
    }
    newParams.set("page", newPagination.page.toString());
    newParams.set("pageSize", newPagination.pageSize.toString());
    setSearchParams(newParams);
    setFilters(newFilters);
    setPaginationState(newPagination);
  };

  const handlePaginationChange = (newPagination: PaginationParam) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPagination.page.toString());
    newParams.set("pageSize", newPagination.pageSize.toString());
    setSearchParams(newParams);
    setPaginationState(newPagination);
  };

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error" sx={{ mt: 3 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  const totalCount = pagination?.total ?? uangJalanReports.length;
  const listSubtitle =
    totalCount > 0
      ? t("reports.listSubtitle" as TranslationKeys, { count: totalCount })
      : t("reports.listSubtitleEmpty" as TranslationKeys);
  const pageTitle = isActionRequired
    ? t("sidebar.actionRequired" as TranslationKeys)
    : t("Uang Jalan Report" as TranslationKeys);

  return (
    <Box sx={{ pb: 4 }}>
      <DetailPageHeader
        title={pageTitle}
        subtitle={listSubtitle}
        actions={
          canCreateReport ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/uang-jalan-reports/new")}
              data-testid={TestId.UJR.ADD_BUTTON}
              sx={headerButtonSx}
            >
              {t("Create Report" as TranslationKeys)}
            </Button>
          ) : undefined
        }
      />

      {loading && uangJalanReports.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <UangJalanReportTable
          reports={actionRequiredEmpty ? [] : uangJalanReports}
          teams={operationsTeams}
          filters={filters}
          loading={loading}
          pagination={pagination}
          onFiltersChange={setFilters}
          onSearch={handleSearch}
          onPaginationChange={handlePaginationChange}
          canCreateReport={canCreateReport}
          onCreateReport={() => navigate("/uang-jalan-reports/new")}
        />
      )}
    </Box>
  );
};

export default UangJalanReportsPage;
